import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { get } from "lib/api";
import { firstMediaUrl } from "lib/media";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import { storefrontThemeForTenant } from "lib/storefrontTheme";
import useStorefrontCopy from "hooks/useStorefrontCopy";

const BRAND = {
  ink: "#111827",
  muted: "#667085",
  line: "rgba(17,24,39,0.1)",
  canvas: "#f7f8fa",
  red: "#d7261e",
  orange: "#f97316",
};

const cardSx = {
  height: "100%",
  border: `1px solid ${BRAND.line}`,
  borderRadius: "18px",
  boxShadow: "0 18px 50px rgba(17,24,39,0.06)",
  overflow: "hidden",
};

function productImage(product) {
  return firstMediaUrl(product?.images);
}

function ProductCard({ product, onOpen }) {
  const image = productImage(product);
  return (
    <Card
      sx={{
        ...cardSx,
        cursor: "pointer",
        transition:
          "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-5px)",
          borderColor: "rgba(215,38,30,0.28)",
          boxShadow: "0 22px 60px rgba(17,24,39,0.1)",
        },
      }}
      onClick={onOpen}
    >
      {image ? (
        <CardMedia
          component="img"
          height="210"
          image={image}
          alt={product.name}
          sx={{ objectFit: "cover" }}
        />
      ) : (
        <MDBox
          height={210}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ bgcolor: "#eef1f4" }}
        >
          <Icon sx={{ fontSize: 58, color: "#98a2b3" }}>
            precision_manufacturing
          </Icon>
        </MDBox>
      )}
      <CardContent sx={{ p: 2.5 }}>
        <MDBox
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1.5}
          mb={1}
        >
          <MDTypography
            variant="h6"
            sx={{
              color: BRAND.ink,
              fontSize: "1rem",
              lineHeight: 1.35,
              fontWeight: 800,
            }}
          >
            {product.name}
          </MDTypography>
          {product.status === "available" ? (
            <Chip
              label="Available"
              size="small"
              sx={{
                bgcolor: "rgba(34,197,94,0.1)",
                color: "#167b3f",
                fontWeight: 800,
                borderRadius: "999px",
              }}
            />
          ) : null}
        </MDBox>
        <MDTypography
          variant="caption"
          sx={{
            display: "block",
            color: BRAND.muted,
            fontFamily: "monospace",
            mb: 1.5,
          }}
        >
          SKU {product.sku || "Pending"}
        </MDTypography>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <MDTypography
            variant="h6"
            sx={{ color: BRAND.red, fontSize: "1.05rem", fontWeight: 900 }}
          >
            {product.unitPrice
              ? `$${parseFloat(product.unitPrice).toFixed(2)}`
              : "Quote"}
          </MDTypography>
          <MDButton
            variant="text"
            sx={{
              color: BRAND.ink,
              fontWeight: 800,
              px: 0,
              minWidth: "auto",
              "&:hover": { color: BRAND.red },
            }}
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            Details
          </MDButton>
        </MDBox>
      </CardContent>
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    images: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    name: PropTypes.string,
    status: PropTypes.string,
    sku: PropTypes.string,
    unitPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

function HomepageAdsCarousel({ ads, onOpen }) {
  const railRef = useRef(null);
  const [previewAd, setPreviewAd] = useState(null);

  if (!ads.length) return null;

  const activateAd = (ad) => {
    if (ad.ctaUrl) {
      onOpen(ad);
      return;
    }
    setPreviewAd(ad);
  };

  const handleCardKeyDown = (event, ad) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    activateAd(ad);
  };

  const scrollCards = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const desktopStep = rail.clientWidth / 3;
    const compactStep = rail.clientWidth * 0.86;
    rail.scrollBy({
      left: direction * (rail.clientWidth >= 900 ? desktopStep : compactStep),
      behavior: "smooth",
    });
  };

  return (
    <MDBox
      sx={{ bgcolor: BRAND.canvas, borderBottom: `1px solid ${BRAND.line}` }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3.5, md: 5 } }}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2.25}
        >
          <MDBox>
            <MDTypography
              variant="overline"
              sx={{
                display: "block",
                color: BRAND.red,
                fontWeight: 900,
                letterSpacing: 0,
                lineHeight: 1.2,
              }}
            >
              Featured
            </MDTypography>
            <MDTypography
              variant="h4"
              sx={{
                color: BRAND.ink,
                fontWeight: 900,
                fontSize: { xs: "1.35rem", md: "1.8rem" },
              }}
            >
              Current offers
            </MDTypography>
          </MDBox>
          {ads.length > 1 ? (
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="text"
                aria-label="Previous offers"
                sx={{
                  minWidth: 42,
                  width: 42,
                  height: 42,
                  p: 0,
                  borderRadius: "999px",
                  color: BRAND.ink,
                  bgcolor: "#fff",
                  border: `1px solid ${BRAND.line}`,
                  boxShadow: "0 10px 28px rgba(17,24,39,0.08)",
                  "&:hover": {
                    bgcolor: "#fff",
                    color: BRAND.red,
                    borderColor: "rgba(215,38,30,0.28)",
                  },
                }}
                onClick={() => scrollCards(-1)}
              >
                <Icon>chevron_left</Icon>
              </MDButton>
              <MDButton
                variant="text"
                aria-label="Next offers"
                sx={{
                  minWidth: 42,
                  width: 42,
                  height: 42,
                  p: 0,
                  borderRadius: "999px",
                  color: BRAND.ink,
                  bgcolor: "#fff",
                  border: `1px solid ${BRAND.line}`,
                  boxShadow: "0 10px 28px rgba(17,24,39,0.08)",
                  "&:hover": {
                    bgcolor: "#fff",
                    color: BRAND.red,
                    borderColor: "rgba(215,38,30,0.28)",
                  },
                }}
                onClick={() => scrollCards(1)}
              >
                <Icon>chevron_right</Icon>
              </MDButton>
            </MDBox>
          ) : null}
        </MDBox>
        <MDBox
          ref={railRef}
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: {
              xs: "86%",
              sm: "calc((100% - 18px) / 2)",
              lg: "calc((100% - 36px) / 3)",
            },
            gap: { xs: 2, md: 2.25 },
            overflowX: "auto",
            scrollSnapType: "x proximity",
            scrollPaddingLeft: { xs: 4, md: 0 },
            px: { xs: 0.5, md: 0 },
            pt: 1,
            pb: 2.5,
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-track": {
              bgcolor: "rgba(17,24,39,0.05)",
              borderRadius: "999px",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(17,24,39,0.22)",
              borderRadius: "999px",
            },
          }}
        >
          {ads.map((ad) => (
            <MDBox
              key={ad.documentId}
              component="article"
              role="button"
              tabIndex={0}
              onClick={() => activateAd(ad)}
              onKeyDown={(event) => handleCardKeyDown(event, ad)}
              sx={{
                position: "relative",
                display: "block",
                minWidth: 0,
                height: { xs: 310, sm: 340, md: 370 },
                borderRadius: "18px",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.72)",
                bgcolor: "#111827",
                boxShadow: "0 18px 42px rgba(17,24,39,0.12)",
                p: 0,
                cursor: "pointer",
                outline: 0,
                scrollSnapAlign: "start",
                textAlign: "left",
                transformOrigin: "center",
                transition:
                  "transform 190ms ease, box-shadow 190ms ease, border-color 190ms ease",
                "&:hover": {
                  transform: "translateY(-6px) scale(1.025)",
                  borderColor: "rgba(215,38,30,0.42)",
                  boxShadow: "0 26px 70px rgba(17,24,39,0.2)",
                  zIndex: 2,
                },
                "&:hover .homepage-ad-image": {
                  transform: "scale(1.08)",
                  filter: "brightness(0.68) saturate(1.05)",
                },
                "&:hover .homepage-ad-overlay": {
                  background:
                    "linear-gradient(180deg, rgba(17,24,39,0.18) 0%, rgba(17,24,39,0.64) 38%, rgba(17,24,39,0.95) 100%)",
                },
                "&:hover .homepage-ad-title, &:hover .homepage-ad-subtitle": {
                  WebkitLineClamp: "unset",
                },
                "&:focus-visible": {
                  boxShadow:
                    "0 0 0 4px rgba(215,38,30,0.24), 0 26px 70px rgba(17,24,39,0.2)",
                  borderColor: "rgba(215,38,30,0.52)",
                },
              }}
            >
              <Box
                component="img"
                className="homepage-ad-image"
                src={ad.image}
                alt={ad.title || "Homepage ad"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  transition: "transform 240ms ease, filter 240ms ease",
                  filter: "brightness(0.82) saturate(1.02)",
                }}
              />
              <MDButton
                variant="text"
                aria-label={`View ${ad.title || "ad"}`}
                sx={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  zIndex: 2,
                  minWidth: "auto",
                  px: 1.4,
                  py: 0.75,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.92)",
                  color: BRAND.ink,
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  lineHeight: 1,
                  boxShadow: "0 10px 28px rgba(0,0,0,0.2)",
                  "&:hover": { bgcolor: "#fff", color: BRAND.red },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setPreviewAd(ad);
                }}
              >
                <Icon sx={{ mr: 0.5, fontSize: "1rem !important" }}>
                  visibility
                </Icon>
                View
              </MDButton>
              <MDBox
                className="homepage-ad-overlay"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  gap: 1,
                  p: { xs: 2.25, md: 2.75 },
                  color: "#fff",
                  background:
                    "linear-gradient(180deg, rgba(17,24,39,0.08) 0%, rgba(17,24,39,0.48) 45%, rgba(17,24,39,0.92) 100%)",
                  transition: "background 190ms ease",
                }}
              >
                <MDTypography
                  component="span"
                  variant="caption"
                  sx={{
                    alignSelf: "flex-start",
                    px: 1.25,
                    py: 0.55,
                    borderRadius: "999px",
                    bgcolor: "rgba(255,255,255,0.18)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    color: "#fff",
                    fontWeight: 900,
                    lineHeight: 1,
                    textTransform: "uppercase",
                    letterSpacing: 0,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  Featured
                </MDTypography>
                <MDTypography
                  className="homepage-ad-title"
                  variant="h4"
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: "1.35rem", md: "1.55rem" },
                    lineHeight: 1.08,
                    textShadow: "0 2px 18px rgba(0,0,0,0.45)",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {ad.title}
                </MDTypography>
                {ad.subtitle ? (
                  <MDTypography
                    className="homepage-ad-subtitle"
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.92)",
                      lineHeight: 1.5,
                      textShadow: "0 1px 12px rgba(0,0,0,0.42)",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                    }}
                  >
                    {ad.subtitle}
                  </MDTypography>
                ) : null}
                <MDBox
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    alignSelf: "flex-start",
                    mt: 0.25,
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                    lineHeight: 1,
                    textShadow: "0 1px 10px rgba(0,0,0,0.35)",
                  }}
                >
                  {ad.ctaLabel || "Shop now"}
                  <Icon sx={{ fontSize: "1rem !important" }}>
                    arrow_forward
                  </Icon>
                </MDBox>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </Container>
      <Dialog
        open={!!previewAd}
        onClose={() => setPreviewAd(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            bgcolor: "#0b1018",
            boxShadow: "0 34px 100px rgba(0,0,0,0.35)",
          },
        }}
      >
        {previewAd ? (
          <MDBox sx={{ position: "relative", color: "#fff" }}>
            <IconButton
              aria-label="Close ad preview"
              onClick={() => setPreviewAd(null)}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 2,
                bgcolor: "rgba(255,255,255,0.92)",
                color: BRAND.ink,
                "&:hover": { bgcolor: "#fff", color: BRAND.red },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: { xs: 320, md: 560 },
                maxHeight: { xs: "70vh", md: "78vh" },
                bgcolor: "#05070a",
              }}
            >
              <Box
                component="img"
                src={previewAd.image}
                alt={previewAd.title || "Homepage ad preview"}
                sx={{
                  width: "100%",
                  height: "100%",
                  maxHeight: { xs: "70vh", md: "78vh" },
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </MDBox>
            <MDBox
              display="flex"
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={2}
              sx={{
                p: { xs: 2.25, md: 3 },
                background:
                  "linear-gradient(180deg, rgba(17,24,39,0.94) 0%, rgba(17,24,39,1) 100%)",
              }}
            >
              <MDBox>
                <MDTypography
                  variant="h4"
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: "1.25rem", md: "1.65rem" },
                  }}
                >
                  {previewAd.title}
                </MDTypography>
                {previewAd.subtitle ? (
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.78)",
                      lineHeight: 1.6,
                      mt: 0.5,
                    }}
                  >
                    {previewAd.subtitle}
                  </MDTypography>
                ) : null}
              </MDBox>
              {previewAd.ctaUrl ? (
                <MDButton
                  variant="contained"
                  color="error"
                  sx={{
                    flexShrink: 0,
                    alignSelf: { xs: "stretch", sm: "center" },
                    bgcolor: BRAND.red,
                    color: "#fff",
                    fontWeight: 900,
                    borderRadius: "999px",
                    px: 2.5,
                    "&:hover": { bgcolor: "#b91c1c" },
                  }}
                  onClick={() => {
                    setPreviewAd(null);
                    onOpen(previewAd);
                  }}
                >
                  {previewAd.ctaLabel || "Shop now"}
                  <Icon sx={{ ml: 0.75 }}>arrow_forward</Icon>
                </MDButton>
              ) : null}
            </MDBox>
          </MDBox>
        ) : null}
      </Dialog>
    </MDBox>
  );
}

HomepageAdsCarousel.propTypes = {
  ads: PropTypes.arrayOf(
    PropTypes.shape({
      documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      ctaLabel: PropTypes.string,
      ctaUrl: PropTypes.string,
      openInNewTab: PropTypes.bool,
      image: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, pluginRole, authLoading } = useAuth();
  const { tenant } = useStorefrontTenant();
  const { t, locale } = useStorefrontCopy();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const storefrontTheme = useMemo(
    () => storefrontThemeForTenant(tenant, locale),
    [tenant, locale],
  );
  const brand = useMemo(
    () => ({
      ...BRAND,
      ink: storefrontTheme.ink,
      muted: storefrontTheme.muted,
      line: storefrontTheme.line,
      canvas: storefrontTheme.canvas,
      red: storefrontTheme.accent,
    }),
    [storefrontTheme],
  );
  const useImageHero =
    storefrontTheme.heroStyle === "image" && !!storefrontTheme.heroImageUrl;
  const isMobileRoute =
    location.pathname === "/m" || location.pathname.startsWith("/m/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;
  const normalizedPluginRole = String(pluginRole || "").toLowerCase();
  const portalUser = isPortalRole(normalizedPluginRole);
  const distributor = isDistributorRole(normalizedPluginRole);
  const authedReady = !!token && !authLoading;

  const primaryCta = useMemo(() => {
    if (!isMobileRoute && authedReady && portalUser) {
      return {
        label: t("common.account", "Account"),
        onClick: () =>
          navigate(distributor ? "/account/valve-bodies" : "/account"),
      };
    }
    return {
      label: t("common.signIn", "Sign in"),
      onClick: () => navigate(toPath("/authentication/sign-in")),
    };
  }, [
    isMobileRoute,
    authedReady,
    portalUser,
    distributor,
    navigate,
    toPath,
    t,
  ]);

  const { data: productsData, isLoading } = useQuery({
    queryKey: ["home-products"],
    queryFn: () =>
      get("/api/storefront/catalog", {
        sort: "updatedAt:desc",
        pagination: { pageSize: 6 },
      }),
    staleTime: 30000,
  });

  const { data: adsData } = useQuery({
    queryKey: ["home-homepage-ads"],
    queryFn: () => get("/api/storefront/homepage-ads"),
    staleTime: 30000,
  });

  const homepageAds = useMemo(
    () =>
      (adsData?.data || [])
        .map((item) => {
          const attrs = item.attributes || item;
          return {
            id: item.id,
            documentId: item.documentId || item.id,
            title: attrs.title || "Homepage ad",
            subtitle: attrs.subtitle || "",
            ctaLabel: attrs.ctaLabel || "",
            ctaUrl: attrs.ctaUrl || "",
            openInNewTab: !!attrs.openInNewTab,
            image: firstMediaUrl(attrs.image),
          };
        })
        .filter((ad) => !!ad.image),
    [adsData],
  );

  useEffect(() => {
    if (!productsData?.data) return;
    setFeaturedProducts(
      productsData.data.map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          status: attrs.status,
        };
      }),
    );
  }, [productsData]);

  const processSteps = [
    ["home.processStepOneTitle", "home.processStepOneText"],
    ["home.processStepTwoTitle", "home.processStepTwoText"],
    ["home.processStepThreeTitle", "home.processStepThreeText"],
    ["home.processStepFourTitle", "home.processStepFourText"],
  ].map(([titleKey, textKey]) => [t(titleKey), t(textKey)]);

  const openHomepageAd = (ad) => {
    const target = String(ad?.ctaUrl || "").trim();
    if (!target) return;
    if (/^https?:\/\//i.test(target)) {
      window.open(
        target,
        ad.openInNewTab ? "_blank" : "_self",
        "noopener,noreferrer",
      );
      return;
    }
    if (ad.openInNewTab) {
      window.open(
        toPath(target.startsWith("/") ? target : `/${target}`),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    navigate(toPath(target.startsWith("/") ? target : `/${target}`));
  };

  if (storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor:
              "radial-gradient(circle at 82% 6%, rgba(181,71,8,0.16), transparent 28%), #080B0E",
            color: "#fff",
          }}
        >
          <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "330px minmax(0,1fr) 300px",
                },
                gridTemplateRows: {
                  xs: "auto",
                  lg: "minmax(660px, calc(100vh - 148px))",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#050608",
                  p: { xs: 2.25, md: 3 },
                  display: "grid",
                  alignContent: "space-between",
                  minHeight: { xs: 420, lg: "auto" },
                  boxShadow: "0 28px 90px rgba(0,0,0,0.26)",
                }}
              >
                <Box>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.48)",
                      fontFamily: "monospace",
                      fontWeight: 900,
                      letterSpacing: "0.16em",
                    }}
                  >
                    STOREFRONT COMMAND
                  </MDTypography>
                  <MDTypography
                    variant="h1"
                    sx={{
                      color: "#fff",
                      fontWeight: 950,
                      fontSize: { xs: "2.35rem", md: "3.7rem" },
                      lineHeight: 0.92,
                      letterSpacing: 0,
                      mt: 1.25,
                    }}
                  >
                    {storefrontTheme.heroTitle}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.68)",
                      lineHeight: 1.75,
                      mt: 2,
                    }}
                  >
                    {storefrontTheme.heroSubtitle}
                  </MDTypography>
                  <Box
                    sx={{
                      mt: 3,
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      borderTop: "1px solid rgba(255,255,255,0.12)",
                      borderBottom: "1px solid rgba(255,255,255,0.12)",
                    }}
                  >
                    {[
                      ["ITEMS", featuredProducts.length || 0],
                      ["FLOW", processSteps.length],
                      ["AUTH", authedReady ? "ON" : "GUEST"],
                    ].map(([label, value], index) => (
                      <Box
                        key={label}
                        sx={{
                          py: 1.5,
                          pr: 1,
                          borderRight:
                            index < 2 ? "1px solid rgba(255,255,255,0.12)" : 0,
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.44)",
                            fontWeight: 900,
                          }}
                        >
                          {label}
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          sx={{ color: "#fff", fontWeight: 950, mt: 0.25 }}
                        >
                          {value}
                        </MDTypography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gap: 1 }}>
                  <MDButton
                    variant="contained"
                    sx={{
                      bgcolor: "#fff",
                      color: "#080B0E",
                      borderRadius: 0,
                      minHeight: 48,
                      fontWeight: 950,
                      boxShadow: "none",
                    }}
                    onClick={() => navigate(toPath("/catalog"))}
                  >
                    Open product matrix
                  </MDButton>
                  <MDButton
                    variant="text"
                    sx={{
                      color: "#fff",
                      borderRadius: 0,
                      minHeight: 44,
                      fontWeight: 900,
                      borderTop: "1px solid rgba(255,255,255,0.14)",
                    }}
                    onClick={primaryCta.onClick}
                  >
                    {primaryCta.label}
                  </MDButton>
                </Box>
              </Box>

              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.94)",
                  color: brand.ink,
                  display: "grid",
                  gridTemplateRows: "auto 1fr",
                  minHeight: { xs: 520, lg: "auto" },
                  boxShadow: "0 34px 100px rgba(0,0,0,0.24)",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                    gap: 2,
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    borderBottom: `1px solid ${brand.line}`,
                  }}
                >
                  <Box>
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: brand.red,
                        fontFamily: "monospace",
                        fontWeight: 900,
                      }}
                    >
                      PRODUCT MATRIX
                    </MDTypography>
                    <MDTypography
                      variant="h3"
                      sx={{ color: brand.ink, fontWeight: 950, lineHeight: 1 }}
                    >
                      Active catalog lanes
                    </MDTypography>
                  </Box>
                  <MDButton
                    variant="text"
                    sx={{
                      color: brand.ink,
                      borderRadius: 0,
                      fontWeight: 950,
                      borderBottom: `2px solid ${brand.red}`,
                    }}
                    onClick={() => navigate(toPath("/catalog"))}
                  >
                    View all
                  </MDButton>
                </Box>

                <Box sx={{ overflow: "auto" }}>
                  {featuredProducts.slice(0, 8).map((product, index) => (
                    <Box
                      key={product.documentId || product.id}
                      onClick={() =>
                        navigate(toPath(`/catalog/${product.documentId}`))
                      }
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "64px minmax(0,1fr)",
                          md: "74px minmax(0,1.2fr) 120px 82px",
                        },
                        gap: 1.5,
                        alignItems: "center",
                        px: { xs: 1.5, md: 2 },
                        py: 1.4,
                        borderBottom: `1px solid ${brand.line}`,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(181,71,8,0.06)" },
                      }}
                    >
                      <Box
                        component={productImage(product) ? "img" : "div"}
                        src={productImage(product) || undefined}
                        sx={{
                          width: 64,
                          height: 58,
                          bgcolor: "#EDF0ED",
                          objectFit: "contain",
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: brand.muted,
                            fontFamily: "monospace",
                            fontWeight: 900,
                          }}
                        >
                          ROW {String(index + 1).padStart(2, "0")} /{" "}
                          {product.sku || "PENDING"}
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          sx={{
                            display: "block",
                            color: brand.ink,
                            fontWeight: 950,
                            lineHeight: 1.2,
                          }}
                        >
                          {product.name}
                        </MDTypography>
                      </Box>
                      <MDTypography
                        variant="caption"
                        sx={{
                          display: { xs: "none", md: "block" },
                          color: brand.muted,
                          fontWeight: 900,
                          textTransform: "uppercase",
                        }}
                      >
                        {product.status || "catalog"}
                      </MDTypography>
                      <Icon
                        sx={{
                          color: brand.red,
                          display: { xs: "none", md: "block" },
                        }}
                      >
                        arrow_forward
                      </Icon>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 2 }}>
                <Box
                  sx={{
                    minHeight: 220,
                    bgcolor: "rgba(255,255,255,0.08)",
                    backgroundImage: storefrontTheme.heroImageUrl
                      ? `linear-gradient(rgba(8,11,14,0.18), rgba(8,11,14,0.72)), url(${storefrontTheme.heroImageUrl})`
                      : "linear-gradient(145deg, rgba(181,71,8,0.28), rgba(255,255,255,0.05))",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "grid",
                    alignContent: "end",
                    p: 2,
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.64)", fontWeight: 900 }}
                  >
                    {t("home.eyebrow")}
                  </MDTypography>
                </Box>

                <Box sx={{ bgcolor: "rgba(255,255,255,0.07)", p: 2 }}>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.48)",
                      fontFamily: "monospace",
                      fontWeight: 900,
                    }}
                  >
                    PROCESS RAIL
                  </MDTypography>
                  <Box sx={{ mt: 1.5 }}>
                    {processSteps.map(([title, text], index) => (
                      <Box
                        key={title}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "34px minmax(0,1fr)",
                          gap: 1.5,
                          pb: 2,
                          mb: 1.5,
                          borderBottom:
                            index < processSteps.length - 1
                              ? "1px solid rgba(255,255,255,0.12)"
                              : 0,
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: brand.red,
                            fontFamily: "monospace",
                            fontWeight: 950,
                          }}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </MDTypography>
                        <Box>
                          <MDTypography
                            variant="body2"
                            sx={{ color: "#fff", fontWeight: 950, lineHeight: 1.25 }}
                          >
                            {title}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{ color: "rgba(255,255,255,0.58)", lineHeight: 1.5 }}
                          >
                            {text}
                          </MDTypography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "commerce") {
    const spotlight = featuredProducts[0];
    const shelfProducts = featuredProducts.slice(1, 7);

    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ minHeight: "100vh", bgcolor: "#F7F9FC", color: brand.ink }}>
          <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1.35fr) minmax(360px, 0.65fr)",
                },
                gap: 2,
                minHeight: { lg: "calc(100vh - 156px)" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  minHeight: { xs: 520, lg: "auto" },
                  bgcolor: "#fff",
                  overflow: "hidden",
                  boxShadow: "0 36px 100px rgba(16,24,40,0.12)",
                }}
              >
                <Box
                  component={storefrontTheme.heroImageUrl ? "img" : "div"}
                  src={storefrontTheme.heroImageUrl || undefined}
                  alt={storefrontTheme.heroTitle}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    bgcolor: "#E8EEF5",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg, rgba(16,24,40,0.78), rgba(16,24,40,0.18) 52%, rgba(16,24,40,0.05))",
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    minHeight: "100%",
                    display: "grid",
                    alignContent: "end",
                    p: { xs: 2.5, md: 4.5 },
                    maxWidth: 760,
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.76)", fontWeight: 900 }}
                  >
                    {t("home.eyebrow")}
                  </MDTypography>
                  <MDTypography
                    variant="h1"
                    sx={{
                      color: "#fff",
                      fontWeight: 950,
                      fontSize: { xs: "3rem", md: "5.8rem" },
                      lineHeight: 0.88,
                      letterSpacing: 0,
                      mt: 1,
                    }}
                  >
                    {storefrontTheme.heroTitle}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: "rgba(255,255,255,0.74)",
                      lineHeight: 1.75,
                      maxWidth: 580,
                      mt: 2,
                    }}
                  >
                    {storefrontTheme.heroSubtitle}
                  </MDTypography>
                  <MDBox display="flex" gap={1.25} flexWrap="wrap" mt={3}>
                    <MDButton
                      variant="contained"
                      sx={{
                        bgcolor: brand.red,
                        color: "#fff",
                        borderRadius: 2,
                        px: 3,
                        py: 1.3,
                        fontWeight: 900,
                        boxShadow: "none",
                      }}
                      onClick={() => navigate(toPath("/catalog"))}
                    >
                      {t("home.primaryCta", "Shop products")}
                    </MDButton>
                    <MDButton
                      variant="text"
                      sx={{
                        color: "#fff",
                        borderRadius: 2,
                        fontWeight: 900,
                        border: "1px solid rgba(255,255,255,0.34)",
                      }}
                      onClick={primaryCta.onClick}
                    >
                      {primaryCta.label}
                    </MDButton>
                  </MDBox>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: "#fff",
                    p: { xs: 2.25, md: 3 },
                    boxShadow: "0 20px 70px rgba(16,24,40,0.09)",
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{ color: brand.red, fontWeight: 900 }}
                  >
                    Featured shelf
                  </MDTypography>
                  {spotlight ? (
                    <Box
                      onClick={() =>
                        navigate(toPath(`/catalog/${spotlight.documentId}`))
                      }
                      sx={{
                        mt: 1.5,
                        display: "grid",
                        gridTemplateColumns: "108px minmax(0,1fr)",
                        gap: 1.5,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component={productImage(spotlight) ? "img" : "div"}
                        src={productImage(spotlight) || undefined}
                        sx={{
                          width: 108,
                          height: 128,
                          bgcolor: "#EEF4F8",
                          objectFit: "contain",
                        }}
                      />
                      <Box>
                        <MDTypography
                          variant="h4"
                          sx={{ color: brand.ink, fontWeight: 950, lineHeight: 1 }}
                        >
                          {spotlight.name}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{ color: brand.muted, fontWeight: 900 }}
                        >
                          {spotlight.sku || "Catalog product"}
                        </MDTypography>
                        <MDButton
                          variant="text"
                          sx={{
                            mt: 1,
                            px: 0,
                            color: brand.red,
                            borderRadius: 0,
                            fontWeight: 950,
                          }}
                        >
                          View product <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                        </MDButton>
                      </Box>
                    </Box>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1.25,
                  }}
                >
                  {shelfProducts.slice(0, 4).map((product, index) => (
                    <Box
                      key={product.documentId || product.id}
                      onClick={() =>
                        navigate(toPath(`/catalog/${product.documentId}`))
                      }
                      sx={{
                        bgcolor: index === 0 ? brand.ink : "#fff",
                        color: index === 0 ? "#fff" : brand.ink,
                        minHeight: 170,
                        p: 1.6,
                        cursor: "pointer",
                        display: "grid",
                        alignContent: "space-between",
                        boxShadow: "0 16px 46px rgba(16,24,40,0.08)",
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: index === 0 ? "rgba(255,255,255,0.58)" : brand.red,
                          fontWeight: 900,
                        }}
                      >
                        {product.sku || "Product"}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        sx={{
                          color: index === 0 ? "#fff" : brand.ink,
                          fontWeight: 950,
                          lineHeight: 1.15,
                        }}
                      >
                        {product.name}
                      </MDTypography>
                    </Box>
                  ))}
                </Box>

                {homepageAds.length ? (
                  <Box
                    onClick={() => openHomepageAd(homepageAds[0])}
                    sx={{
                      bgcolor: "#fff",
                      minHeight: 140,
                      backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.86), rgba(255,255,255,0.2)), url(${homepageAds[0].image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      p: 2,
                      cursor: "pointer",
                    }}
                  >
                    <MDTypography
                      variant="h5"
                      sx={{ color: brand.ink, fontWeight: 950 }}
                    >
                      {homepageAds[0].title}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      sx={{ color: brand.muted, fontWeight: 800 }}
                    >
                      {homepageAds[0].ctaLabel || "Open"}
                    </MDTypography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: "#080B0E",
            color: "#fff",
          }}
        >
          <MDBox
            sx={{
              bgcolor:
                "radial-gradient(circle at 82% 8%, rgba(181,71,8,0.20), transparent 32%), linear-gradient(135deg, #080B0E 0%, #10151B 52%, #171A18 100%)",
              color: "#fff",
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
              <Grid container spacing={4} alignItems="stretch">
                <Grid item xs={12} md={7}>
                  <MDTypography
                    variant="overline"
                    sx={{
                      color: "rgba(255,255,255,0.66)",
                      fontWeight: 900,
                      letterSpacing: "0.16em",
                    }}
                  >
                    {t("home.eyebrow")}
                  </MDTypography>
                  <MDTypography
                    variant="h1"
                    sx={{
                      mt: 1.25,
                      mb: 2,
                      color: "#fff",
                      fontWeight: 950,
                      fontSize: { xs: "2.35rem", md: "4.35rem" },
                      lineHeight: 0.96,
                      letterSpacing: 0,
                      maxWidth: 820,
                    }}
                  >
                    {storefrontTheme.heroTitle}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: "rgba(255,255,255,0.70)",
                      fontSize: { xs: "1rem", md: "1.08rem" },
                      lineHeight: 1.65,
                      maxWidth: 680,
                    }}
                  >
                    {storefrontTheme.heroSubtitle}
                  </MDTypography>
                  <MDBox display="flex" gap={1.25} flexWrap="wrap" mt={3.5}>
                    <MDButton
                      variant="contained"
                      sx={{
                        bgcolor: brand.red,
                        color: "#fff",
                        borderRadius: "999px",
                        px: 3,
                        py: 1.25,
                        fontWeight: 900,
                        boxShadow: "none",
                      }}
                      onClick={() => navigate(toPath("/catalog"))}
                    >
                      {t("home.primaryCta", "Shop products")}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      sx={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.28)",
                        borderRadius: "999px",
                        px: 3,
                        py: 1.25,
                        fontWeight: 900,
                      }}
                      onClick={primaryCta.onClick}
                    >
                      {primaryCta.label}
                    </MDButton>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={5}>
                  <MDBox
                    sx={{
                      height: "100%",
                      minHeight: 360,
                      border: "1px solid rgba(255,255,255,0.16)",
                      borderRadius: "28px",
                      bgcolor: "rgba(255,255,255,0.07)",
                      overflow: "hidden",
                      boxShadow: "0 34px 90px rgba(0,0,0,0.28)",
                      backdropFilter: "blur(18px)",
                      display: "grid",
                      gridTemplateRows: "1fr auto",
                    }}
                  >
                    {storefrontTheme.heroImageUrl ? (
                      <MDBox
                        component="img"
                        src={storefrontTheme.heroImageUrl}
                        alt={storefrontTheme.heroTitle}
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: 270,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <MDBox
                        display="grid"
                        sx={{
                          placeItems: "center",
                          minHeight: 270,
                          background:
                            "linear-gradient(135deg, rgba(181,71,8,0.22), rgba(255,255,255,0.04))",
                        }}
                      >
                        <Icon
                          sx={{ fontSize: 86, color: "rgba(255,255,255,0.38)" }}
                        >
                          precision_manufacturing
                        </Icon>
                      </MDBox>
                    )}
                    <Grid
                      container
                      sx={{ borderTop: "1px solid rgba(255,255,255,0.14)" }}
                    >
                      {[
                        ["SKU", featuredProducts.length || 0],
                        ["FLOW", processSteps.length],
                        ["READY", isLoading ? "..." : featuredProducts.length],
                      ].map(([label, value]) => (
                        <Grid item xs={4} key={label}>
                          <MDBox
                            sx={{
                              p: 1.5,
                              borderRight: "1px solid rgba(255,255,255,0.10)",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: "rgba(255,255,255,0.55)",
                                fontWeight: 900,
                              }}
                            >
                              {label}
                            </MDTypography>
                            <MDTypography
                              variant="h5"
                              sx={{ color: "#fff", fontWeight: 900 }}
                            >
                              {value}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                </Grid>
              </Grid>
            </Container>
          </MDBox>

          <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
            {homepageAds.length ? (
              <MDBox sx={{ mb: 4 }}>
                <HomepageAdsCarousel
                  ads={homepageAds}
                  onOpen={openHomepageAd}
                />
              </MDBox>
            ) : null}
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <MDBox
                  sx={{
                    position: { md: "sticky" },
                    top: 110,
                    border: "1px solid rgba(255,255,255,0.12)",
                    bgcolor: "rgba(255,255,255,0.07)",
                    p: 3,
                    borderRadius: "24px",
                    boxShadow: "0 24px 70px rgba(0,0,0,0.18)",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    sx={{ color: "#fff", fontWeight: 950 }}
                  >
                    {t("catalog.title", "Products")}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.66)", mt: 1.25 }}
                  >
                    {t(
                      "catalog.subtitle",
                      "Search available products with current tenant pricing.",
                    )}
                  </MDTypography>
                  <MDButton
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: "#fff",
                      color: "#080B0E",
                      borderRadius: "999px",
                      fontWeight: 900,
                    }}
                    onClick={() => navigate(toPath("/catalog"))}
                  >
                    {t("common.catalog", "Catalog")}
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1.5}>
                  {featuredProducts.slice(0, 6).map((product) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={product.documentId || product.id}
                    >
                      <MDBox
                        onClick={() =>
                          navigate(toPath(`/catalog/${product.documentId}`))
                        }
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "92px minmax(0,1fr) auto",
                          gap: 1.5,
                          alignItems: "center",
                          border: "1px solid rgba(255,255,255,0.10)",
                          bgcolor: "rgba(255,255,255,0.92)",
                          borderRadius: "20px",
                          p: 1.25,
                          cursor: "pointer",
                          boxShadow: "0 20px 55px rgba(0,0,0,0.16)",
                        }}
                      >
                        <MDBox
                          component={productImage(product) ? "img" : "div"}
                          src={productImage(product) || undefined}
                          sx={{
                            width: 92,
                            height: 78,
                            objectFit: "cover",
                            bgcolor: brand.canvas,
                            borderRadius: "14px",
                          }}
                        />
                        <MDBox sx={{ minWidth: 0 }}>
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: brand.muted,
                              fontFamily: "monospace",
                              fontWeight: 900,
                            }}
                          >
                            {product.sku || "PENDING"}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            sx={{
                              display: "block",
                              color: brand.ink,
                              fontWeight: 900,
                              lineHeight: 1.2,
                            }}
                          >
                            {product.name}
                          </MDTypography>
                        </MDBox>
                        <Icon sx={{ color: brand.red }}>arrow_forward</Icon>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            <MDBox id="process" sx={{ mt: 5 }}>
              <Grid container spacing={1.5}>
                {processSteps.map(([title, text], index) => (
                  <Grid item xs={12} md={3} key={title}>
                    <MDBox
                      sx={{
                        border: "1px solid rgba(255,255,255,0.10)",
                        bgcolor: "rgba(255,255,255,0.07)",
                        borderRadius: "20px",
                        p: 2,
                        minHeight: 170,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.56)",
                          fontWeight: 900,
                        }}
                      >
                        STEP {index + 1}
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        sx={{ color: "#fff", fontWeight: 900, mt: 0.75 }}
                      >
                        {title}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.66)", mt: 1 }}
                      >
                        {text}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "commerce") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ minHeight: "100vh", bgcolor: "#F7F9FC", color: brand.ink }}>
          <MDBox
            sx={{
              bgcolor: "linear-gradient(180deg, #FFFFFF 0%, #F7F9FC 100%)",
              borderBottom: `1px solid ${brand.line}`,
              overflow: "hidden",
            }}
          >
            <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Chip
                    label={t("home.eyebrow")}
                    sx={{
                      bgcolor: `${brand.red}14`,
                      color: brand.red,
                      fontWeight: 900,
                      mb: 2,
                      borderRadius: "999px",
                    }}
                  />
                  <MDTypography
                    variant="h1"
                    sx={{
                      color: brand.ink,
                      fontWeight: 950,
                      fontSize: { xs: "2.45rem", md: "4.4rem" },
                      lineHeight: 0.96,
                      letterSpacing: 0,
                    }}
                  >
                    {storefrontTheme.heroTitle}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: brand.muted,
                      mt: 2,
                      lineHeight: 1.75,
                      fontSize: "1.08rem",
                    }}
                  >
                    {storefrontTheme.heroSubtitle}
                  </MDTypography>
                  <MDBox display="flex" gap={1.25} flexWrap="wrap" mt={3}>
                    <MDButton
                      variant="contained"
                      sx={{
                        bgcolor: brand.red,
                        color: "#fff",
                        borderRadius: "999px",
                        px: 3,
                        py: 1.35,
                        fontWeight: 900,
                        boxShadow: "0 18px 42px rgba(16,24,40,0.14)",
                      }}
                      onClick={() => navigate(toPath("/catalog"))}
                    >
                      {t("home.primaryCta", "Shop products")}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      sx={{
                        color: brand.ink,
                        borderColor: brand.line,
                        borderRadius: "999px",
                        px: 3,
                        py: 1.35,
                        fontWeight: 900,
                        bgcolor: "#fff",
                      }}
                      onClick={primaryCta.onClick}
                    >
                      {primaryCta.label}
                    </MDButton>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={7}>
                  <MDBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "0.8fr 1.2fr" },
                      gridTemplateRows: { xs: "auto", md: "230px 230px" },
                      gap: 2,
                    }}
                  >
                    <MDBox
                      component={storefrontTheme.heroImageUrl ? "img" : "div"}
                      src={storefrontTheme.heroImageUrl || undefined}
                      alt={storefrontTheme.heroTitle}
                      sx={{
                        gridRow: { md: "1 / span 2" },
                        width: "100%",
                        height: { xs: 320, md: "100%" },
                        objectFit: "cover",
                        bgcolor: "#eef2f7",
                        borderRadius: "32px",
                        boxShadow: "0 36px 90px rgba(16,24,40,0.13)",
                      }}
                    />
                    {featuredProducts.slice(0, 2).map((product, index) => (
                      <MDBox
                        key={product.documentId || product.id}
                        onClick={() =>
                          navigate(toPath(`/catalog/${product.documentId}`))
                        }
                        sx={{
                          bgcolor: index === 0 ? brand.ink : "#fff",
                          color: index === 0 ? "#fff" : brand.ink,
                          border: `1px solid ${brand.line}`,
                          borderRadius: "28px",
                          p: 2.4,
                          boxShadow: "0 20px 55px rgba(16,24,40,0.10)",
                          cursor: "pointer",
                          display: "grid",
                          alignContent: "space-between",
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          sx={{
                            color:
                              index === 0
                                ? "rgba(255,255,255,0.66)"
                                : brand.red,
                            fontWeight: 900,
                          }}
                        >
                          Featured
                        </MDTypography>
                        <MDBox>
                          <MDTypography
                            variant="h5"
                            sx={{
                              color: index === 0 ? "#fff" : brand.ink,
                              fontWeight: 950,
                              mt: 0.5,
                            }}
                          >
                            {product.name}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{
                              color:
                                index === 0
                                  ? "rgba(255,255,255,0.62)"
                                  : brand.muted,
                            }}
                          >
                            {product.sku || "Catalog product"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                </Grid>
              </Grid>
            </Container>
          </MDBox>

          <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
            {homepageAds.length ? (
              <MDBox sx={{ mb: 5 }}>
                <HomepageAdsCarousel
                  ads={homepageAds}
                  onOpen={openHomepageAd}
                />
              </MDBox>
            ) : null}
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="end"
              mb={2.5}
            >
              <MDBox>
                <MDTypography
                  variant="h3"
                  sx={{ color: brand.ink, fontWeight: 950 }}
                >
                  {t("catalog.title", "Products")}
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: brand.muted }}>
                  {t(
                    "catalog.subtitle",
                    "Search available products with current tenant pricing.",
                  )}
                </MDTypography>
              </MDBox>
              <MDButton
                variant="text"
                sx={{
                  color: brand.red,
                  fontWeight: 900,
                  borderRadius: "999px",
                }}
                onClick={() => navigate(toPath("/catalog"))}
              >
                {t("common.catalog", "Catalog")}{" "}
                <Icon sx={{ ml: 0.75 }}>arrow_forward</Icon>
              </MDButton>
            </MDBox>
            <Grid container spacing={2.5}>
              {featuredProducts.slice(0, 6).map((product) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={product.documentId || product.id}
                >
                  <ProductCard
                    product={product}
                    onOpen={() =>
                      navigate(toPath(`/catalog/${product.documentId}`))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }
  return (
    <StorefrontLayout maxWidth={null}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: storefrontTheme.pageBg,
          color: brand.ink,
        }}
      >
        {useImageHero ? (
          <MDBox
            sx={{
              minHeight: { xs: 560, md: 680 },
              display: "flex",
              alignItems: "center",
              color: "#fff",
              borderBottom: `1px solid ${brand.line}`,
              backgroundImage: `linear-gradient(90deg, rgba(15,15,18,0.86) 0%, rgba(15,15,18,0.62) 42%, rgba(15,15,18,0.20) 100%), url(${storefrontTheme.heroImageUrl})`,
              backgroundPosition: "center",
              backgroundSize: "cover",
              position: "relative",
            }}
          >
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
              <MDTypography
                variant="overline"
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  letterSpacing: "0.14em",
                  fontSize: "0.75rem",
                  opacity: 0.82,
                }}
              >
                {t("home.eyebrow")}
              </MDTypography>
              <MDTypography
                variant="h1"
                sx={{
                  mt: 1.5,
                  mb: 2.5,
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: "2.7rem", md: "5.35rem" },
                  lineHeight: 0.96,
                  letterSpacing: 0,
                  maxWidth: 820,
                  textShadow: "0 8px 32px rgba(0,0,0,0.28)",
                }}
              >
                {storefrontTheme.heroTitle}
              </MDTypography>
              <MDTypography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.84)",
                  fontSize: { xs: "1rem", md: "1.22rem" },
                  lineHeight: 1.75,
                  maxWidth: 620,
                }}
              >
                {storefrontTheme.heroSubtitle}
              </MDTypography>
              <MDBox display="flex" gap={1.5} flexWrap="wrap" mt={4}>
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: brand.red,
                    color: "#fff",
                    borderRadius: `${storefrontTheme.buttonRadius}px`,
                    px: 3,
                    py: 1.35,
                    fontWeight: 900,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: brand.red,
                      boxShadow: "none",
                      filter: "brightness(0.92)",
                    },
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  {t("common.browseCatalog", "Browse catalog")}
                </MDButton>
                <MDButton
                  variant="outlined"
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.48)",
                    borderRadius: `${storefrontTheme.buttonRadius}px`,
                    px: 3,
                    py: 1.35,
                    fontWeight: 900,
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.10)",
                    },
                  }}
                  onClick={() =>
                    document
                      .getElementById("process")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  {t("common.seeProcess", "See process")}
                </MDButton>
              </MDBox>
            </Container>
          </MDBox>
        ) : (
          <MDBox
            sx={{
              bgcolor: storefrontTheme.surface,
              borderBottom: `1px solid ${brand.line}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
          <Container
            maxWidth={storefrontTheme.preset === "commerce" ? "xl" : "lg"}
            sx={{
              py: { xs: 7, md: storefrontTheme.density === "compact" ? 8 : 10 },
            }}
          >
            <Grid
              container
              spacing={{
                xs: 4,
                md: storefrontTheme.density === "open" ? 8 : 7,
              }}
              alignItems="center"
            >
              <Grid item xs={12} md={7}>
                <MDTypography
                  variant="overline"
                  sx={{
                    color: brand.red,
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    fontSize: "0.75rem",
                  }}
                >
                  {t("home.eyebrow")}
                </MDTypography>
                <MDTypography
                  variant="h1"
                  sx={{
                    mt: 1.5,
                    mb: 2.5,
                    color: brand.ink,
                    fontWeight: 900,
                    fontSize: { xs: "2.6rem", md: "4.8rem" },
                    lineHeight: 0.98,
                    letterSpacing: 0,
                    maxWidth: 780,
                  }}
                >
                  {storefrontTheme.heroTitle}
                </MDTypography>
                <MDTypography
                  variant="body1"
                  sx={{
                    color: brand.muted,
                    fontSize: { xs: "1rem", md: "1.18rem" },
                    lineHeight: 1.75,
                    maxWidth: 650,
                  }}
                >
                  {storefrontTheme.heroSubtitle}
                </MDTypography>
                <MDBox display="flex" gap={1.5} flexWrap="wrap" mt={4}>
                  <MDButton
                    variant="contained"
                    sx={{
                      bgcolor: brand.red,
                      color: "#fff",
                      borderRadius: `${storefrontTheme.buttonRadius}px`,
                      px: 3,
                      py: 1.35,
                      fontWeight: 900,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: brand.red,
                        boxShadow: "none",
                        filter: "brightness(0.92)",
                      },
                    }}
                    onClick={() => navigate(toPath("/catalog"))}
                  >
                    {t("common.browseCatalog", "Browse catalog")}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    sx={{
                      color: brand.ink,
                      borderColor: "rgba(17,24,39,0.22)",
                      borderRadius: `${storefrontTheme.buttonRadius}px`,
                      px: 3,
                      py: 1.35,
                      fontWeight: 900,
                      "&:hover": {
                        borderColor: brand.red,
                        bgcolor: "rgba(181,71,8,0.06)",
                      },
                    }}
                    onClick={() =>
                      document
                        .getElementById("process")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    {t("common.seeProcess", "See process")}
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={5}>
                {storefrontTheme.heroImageUrl ? (
                  <MDBox
                    component="img"
                    src={storefrontTheme.heroImageUrl}
                    alt={storefrontTheme.heroTitle}
                    sx={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      border: `1px solid ${brand.line}`,
                      borderRadius: `${storefrontTheme.cardRadius}px`,
                      boxShadow: storefrontTheme.strongShadow,
                    }}
                  />
                ) : (
                  <MDBox
                    sx={{
                      border: `1px solid ${brand.line}`,
                      borderRadius: `${storefrontTheme.cardRadius}px`,
                      bgcolor: brand.canvas,
                      p: { xs: 2.5, md: 3 },
                      boxShadow: storefrontTheme.strongShadow,
                    }}
                  >
                    <Grid container spacing={2}>
                      {processSteps.map(([title, text]) => (
                        <Grid item xs={6} key={title}>
                          <MDBox
                            sx={{
                              bgcolor: storefrontTheme.surface,
                              border: `1px solid ${brand.line}`,
                              borderRadius: `${Math.max(
                                6,
                                storefrontTheme.panelRadius,
                              )}px`,
                              p: 2,
                            }}
                          >
                            <MDTypography
                              variant="h5"
                              sx={{
                                color: brand.ink,
                                fontWeight: 900,
                                fontSize: "1.1rem",
                              }}
                            >
                              {title}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{ color: brand.muted, lineHeight: 1.5 }}
                            >
                              {text}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </Container>
          </MDBox>
        )}

        {homepageAds.length > 0 ? (
          <HomepageAdsCarousel ads={homepageAds} onOpen={openHomepageAd} />
        ) : (
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
            <Grid container spacing={3}>
              {[
                ["home.featureOneTitle", "home.featureOneText"],
                ["home.featureTwoTitle", "home.featureTwoText"],
                ["home.featureThreeTitle", "home.featureThreeText"],
              ].map(([titleKey, textKey]) => {
                const title = t(titleKey);
                const text = t(textKey);
                return (
                  <Grid item xs={12} md={4} key={title}>
                    <MDBox sx={{ borderTop: `2px solid ${brand.red}`, pt: 2 }}>
                      <MDTypography
                        variant="h5"
                        sx={{
                          color: brand.ink,
                          fontWeight: 900,
                          fontSize: "1.15rem",
                          mb: 1,
                        }}
                      >
                        {title}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={{ color: brand.muted, lineHeight: 1.7 }}
                      >
                        {text}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        )}

        <MDBox
          id="process"
          sx={{
            bgcolor: brand.canvas,
            py: { xs: 6, md: 9 },
            borderBlock: `1px solid ${brand.line}`,
          }}
        >
          <Container maxWidth="lg">
            <MDBox mb={4}>
              <MDTypography
                variant="h2"
                sx={{
                  color: brand.ink,
                  fontWeight: 900,
                  fontSize: { xs: "2rem", md: "3rem" },
                  letterSpacing: 0,
                }}
              >
                {t("home.processTitle")}
              </MDTypography>
              <MDTypography
                variant="body1"
                sx={{
                  color: brand.muted,
                  mt: 1,
                  maxWidth: 660,
                  lineHeight: 1.7,
                }}
              >
                {t("home.processSubtitle")}
              </MDTypography>
            </MDBox>
            <Grid container spacing={2.5}>
              {processSteps.map(([title, text], index) => (
                <Grid item xs={12} sm={6} md={3} key={title}>
                  <MDBox
                    sx={{
                      ...cardSx,
                      bgcolor: storefrontTheme.surface,
                      borderColor: brand.line,
                      borderRadius: `${storefrontTheme.cardRadius}px`,
                      boxShadow: storefrontTheme.shadow,
                      p: 2.5,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{ color: brand.red, fontWeight: 900 }}
                    >
                      0{index + 1}
                    </MDTypography>
                    <MDTypography
                      variant="h5"
                      sx={{
                        color: brand.ink,
                        fontWeight: 900,
                        fontSize: "1.1rem",
                        my: 1,
                      }}
                    >
                      {title}
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      sx={{ color: brand.muted, lineHeight: 1.65 }}
                    >
                      {text}
                    </MDTypography>
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MDBox>

        <Container id="shop" maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            justifyContent="space-between"
            gap={3}
            mb={4}
          >
            <MDBox>
              <MDTypography
                variant="h2"
                sx={{
                  color: BRAND.ink,
                  fontWeight: 900,
                  fontSize: { xs: "2rem", md: "3rem" },
                  letterSpacing: 0,
                }}
              >
                {t("catalog.title", "Featured catalog")}
              </MDTypography>
              <MDTypography variant="body1" sx={{ color: brand.muted, mt: 1 }}>
                {t("catalog.subtitle", "Current products, ready for review.")}
              </MDTypography>
            </MDBox>
            <MDButton
              variant="outlined"
              sx={{
                borderColor: brand.line,
                color: brand.ink,
                borderRadius: `${storefrontTheme.buttonRadius}px`,
                fontWeight: 900,
              }}
              onClick={() => navigate(toPath("/catalog"))}
            >
              {t("common.browseCatalog", "View all")}
            </MDButton>
          </MDBox>

          {isLoading ? (
            <MDBox py={8} textAlign="center">
              <MDTypography sx={{ color: brand.muted }}>
                {t("common.loading", "Loading...")}
              </MDTypography>
            </MDBox>
          ) : featuredProducts.length === 0 ? (
            <MDBox py={8} textAlign="center">
              <MDTypography
                variant="h6"
                sx={{ color: brand.ink, fontWeight: 900 }}
              >
                {t("catalog.emptyTitle", "No products available right now")}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: brand.muted, mt: 1 }}>
                {t(
                  "catalog.emptyText",
                  "Check back soon for updated inventory.",
                )}
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={3}>
              {featuredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <ProductCard
                    product={product}
                    onOpen={() =>
                      navigate(toPath(`/catalog/${product.documentId}`))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>

        <MDBox
          sx={{
            bgcolor: brand.ink,
            color: "#fff",
            py: 5,
            display: isMobileRoute ? "none" : "block",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <MDTypography
                  variant="h5"
                  sx={{ color: "#fff", fontWeight: 900, mb: 1 }}
                >
                  {storefrontTheme.heroTitle}
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}
                >
                  {storefrontTheme.heroSubtitle}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={5}>
                <MDBox
                  display="flex"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <MDButton
                    variant="text"
                    sx={{ color: "#fff", fontWeight: 900 }}
                    onClick={primaryCta.onClick}
                  >
                    {primaryCta.label}
                  </MDButton>
                  <MDButton
                    variant="text"
                    sx={{ color: "#fff", fontWeight: 900 }}
                    onClick={() => navigate(toPath("/catalog"))}
                  >
                    {t("common.catalog", "Catalog")}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </Container>
        </MDBox>
      </Box>
    </StorefrontLayout>
  );
}
